import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { AuthService } from "@infrastructure/services";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNavigate } from "react-router-dom";
import {
  cleanupRbacDevScenario,
  getOrCreateRbacDevScenario,
  getRbacDevErrorMessage,
  isRbacDevAdminSession,
  loadRbacDevScenario,
  RBAC_DEV_PASSWORD,
  RBAC_DEV_PERSONAS,
  RBAC_DEV_PROJECT_NAME,
  RBAC_DEV_TEAM_NAME,
  type DevPersona,
  type RbacDevScenario,
} from "./rbacDevScenario";

const panelStyle: React.CSSProperties = {
  position: "fixed",
  right: 18,
  bottom: 18,
  zIndex: 10_000,
  width: 380,
  maxHeight: "80vh",
  overflowY: "auto",
  padding: 16,
  border: "1px solid #64748b",
  borderRadius: 8,
  background: "#fff",
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25)",
  color: "#172033",
  fontFamily: "Arial, sans-serif",
  fontSize: 14,
};

export const RbacPersonaPanel: React.FC = () => {
  const authService = useRef(useInjection(AuthService)).current;
  const navigate = useNavigate();
  const currentUsername = useUserManagementStore((state) => state.userSession.username);
  const accessToken = useUserManagementStore((state) => state.userSession.accessToken);
  // Deployment roles are session-wide; the scoped permission cache may not have loaded yet.
  const sessionRole = useMemo(() => {
    try {
      return { isAdmin: isRbacDevAdminSession(accessToken), error: "" };
    } catch (error) {
      return { isAdmin: false, error: `Could not read the current session role: ${getRbacDevErrorMessage(error)}` };
    }
  }, [accessToken]);
  const [isOpen, setIsOpen] = useState(false);
  const [scenario, setScenario] = useState<RbacDevScenario | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const disabled = isBusy || isLoading;
  // AuthService stores the JWT name, which can be the display name rather than the login username.
  const currentPersona = scenario?.personas.find((persona) => currentUsername === persona.username || currentUsername === persona.displayName);

  useEffect(() => {
    if (!isOpen || !currentUsername) return;
    let isCancelled = false;
    setScenario(null);
    setIsLoading(true);
    setErrorMessage("");
    setStatusMessage("");
    const load = async () => {
      try {
        let token = useUserManagementStore.getState().userSession.accessToken;
        if (authService.isTokenExpired(token)) token = await authService.refreshAccessToken();
        if (isCancelled) return;
        if (!token) throw new Error("Log in before loading the RBAC development scenario.");
        const loaded = await loadRbacDevScenario(token);
        if (!isCancelled) setScenario(loaded);
      } catch (error) {
        if (!isCancelled) setErrorMessage(`Scenario discovery failed: ${getRbacDevErrorMessage(error)}`);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, currentUsername, refreshVersion, authService]);

  const applyScenario = (loaded: RbacDevScenario, shouldSelectScenario: boolean) => {
    setScenario(loaded);
    const workspace = useWorkspaceContextStore.getState();
    workspace.setWorkspace(loaded.teams, loaded.projects);
    if (!shouldSelectScenario || !loaded.team || !loaded.project) return;
    workspace.setActiveTeamContext(loaded.team.id, loaded.project.id);
    navigate(`/?${new URLSearchParams({ teamId: loaded.team.id, projectId: loaded.project.id })}`, { replace: true });
  };

  const manageScenario = async (action: "create" | "cleanup") => {
    setErrorMessage("");
    setStatusMessage("");
    setIsBusy(true);
    const identity = useUserManagementStore.getState().userSession.username;
    try {
      let token = useUserManagementStore.getState().userSession.accessToken;
      if (authService.isTokenExpired(token)) token = await authService.refreshAccessToken();
      if (!token) throw new Error("Your administrator session is unavailable. Log in again.");
      if (!isRbacDevAdminSession(token)) throw new Error("Log in as an administrator before creating or cleaning up the scenario.");
      const loaded = action === "create" ? await getOrCreateRbacDevScenario(token) : await cleanupRbacDevScenario(token);
      if (identity !== useUserManagementStore.getState().userSession.username) return;
      applyScenario(loaded, action === "create");
      if (action === "cleanup") navigate("/", { replace: true });
      setStatusMessage(
        action === "create"
          ? "Scenario ready. Existing accounts and resources were reused; missing resources and role assignments were restored."
          : "Scenario and development users deleted. You are still signed in as admin.",
      );
    } catch (error) {
      setErrorMessage(
        `${action === "create" ? "Setup" : "Cleanup"} failed: ${getRbacDevErrorMessage(error)}\nAny remaining resources can be rediscovered after a refresh. Retry as admin; existing resources are not rolled back.`,
      );
    } finally {
      setIsBusy(false);
    }
  };

  const switchPersona = async (persona: DevPersona) => {
    setIsBusy(true);
    setErrorMessage("");
    setStatusMessage("");
    try {
      await authService.signInWithCredentials({ username: persona.username, password: persona.password });
      const token = useUserManagementStore.getState().userSession.accessToken;
      if (!token) throw new Error("Persona sign-in did not return an access token.");
      applyScenario(await loadRbacDevScenario(token), true);
    } catch (error) {
      setErrorMessage(`Could not switch to ${persona.username}: ${getRbacDevErrorMessage(error)}`);
    } finally {
      sessionStorage.removeItem("signInProgress");
      setIsBusy(false);
    }
  };

  return (
    <>
      {isOpen ? (
        <aside aria-label="RBAC development tools" style={panelStyle}>
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <strong>RBAC dev tools</strong>
            <button type="button" aria-label="Close RBAC development tools" onClick={() => setIsOpen(false)}>
              Close
            </button>
          </header>
          <p>
            Backend: <code>{runtimeConfig.API_BASE_URL}</code>
          </p>
          <p role="note" style={{ color: "#9a3412" }}>
            These are shared, real development accounts with a public, fixed password. Never use them in production or give them administrator privileges. Reopening this panel discovers existing data;
            no administrator credentials are stored.
          </p>
          <p>
            <strong>Team:</strong> {RBAC_DEV_TEAM_NAME}
            <br />
            <strong>Project:</strong> {RBAC_DEV_PROJECT_NAME}
            <br />
            <strong>Current user:</strong> {currentPersona?.username ?? currentUsername ?? "signed out"}
            <br />
            <strong>Development password:</strong> <code>{RBAC_DEV_PASSWORD}</code>
          </p>
          <Button type="button" disabled={disabled} onClick={() => setRefreshVersion((value) => value + 1)}>
            Refresh scenario
          </Button>
          {isLoading && <p role="status">Looking for the existing scenario...</p>}
          {scenario && (
            <p>
              {scenario.personas.length} of {RBAC_DEV_PERSONAS.length} development users found.{" "}
              {scenario.team && scenario.project ? "Team and project found." : "Log in as admin to create or repair the scenario if it is missing."}
            </p>
          )}
          <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
            {scenario?.personas.map((persona) => (
              <Button key={persona.id} type="button" disabled={disabled || currentPersona?.id === persona.id} onClick={() => void switchPersona(persona)}>
                {persona.username} · Team {persona.teamRole} / Project {persona.projectRole}
              </Button>
            ))}
          </div>
          {sessionRole.isAdmin ? (
            <form
              style={{ marginTop: 16 }}
              onSubmit={(event) => {
                event.preventDefault();
                void manageScenario("create");
              }}
            >
              <div style={{ display: "grid", gap: 8 }}>
                <Button type="submit" themeColor="primary" disabled={disabled || !scenario}>
                  Get or create RBAC scenario
                </Button>
                <Button type="button" themeColor="error" disabled={disabled || !scenario} onClick={() => void manageScenario("cleanup")}>
                  Clean up RBAC scenario
                </Button>
              </div>
            </form>
          ) : (
            <p>To create, repair or clean up the scenario, sign out and log in normally as an administrator. Persona switching does not retain an administrator session.</p>
          )}
          {statusMessage && <p role="status">{statusMessage}</p>}
          {sessionRole.error && <p role="alert">{sessionRole.error}</p>}
          {errorMessage && (
            <pre role="alert" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "#b91c1c", marginTop: 12 }}>
              {errorMessage}
            </pre>
          )}
        </aside>
      ) : (
        <button type="button" onClick={() => setIsOpen(true)} style={{ ...panelStyle, width: "auto", cursor: "pointer", fontWeight: 700 }}>
          RBAC dev tools
        </button>
      )}
    </>
  );
};
